import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import "./styles/dashboard.css";
import SalesOverviewGraph from "./SalesOverviewGraph";
import { getSalesOverview } from "../../services/dashboard-api";

const SalesOverview = () => {
  const [chartData, setChartData] = useState({ series: [], labels: [] });
  const [filter, setFilter] = useState("last_7_days");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Custom date picker states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  const filterOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "This Month", value: "this_month" },
    { label: "Custom Date", value: "custom_date" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Helper to format date as YYYY-MM-DD in local timezone
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        let response;
        if (filter === 'custom_date' && selectedStartDate && selectedEndDate) {
          // Use custom date range
          const params = {
            start_date: formatLocalDate(selectedStartDate),
            end_date: formatLocalDate(selectedEndDate),
          };
          response = await getSalesOverview(null, params);
        } else {
          // Use predefined filter
          response = await getSalesOverview(filter);
        }

        if (response.status === "success") {
          const formattedLabels = response.data.labels.map(label => {
            const date = new Date(label);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          });
          setChartData({
            series: response.data.data,
            labels: formattedLabels,
          });
        }
      } catch (error) {
        console.error("Error fetching sales overview:", error);
      }
    };
    fetchData();
  }, [filter, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.target)
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Date picker helper functions
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date > today;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date) => {
    if (!selectedStartDate && !selectedEndDate) return false;
    return (
      (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
      (selectedEndDate && date.getTime() === selectedEndDate.getTime())
    );
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else if (selectedStartDate && !selectedEndDate) {
      if (date < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
      } else {
        setSelectedEndDate(date);
      }
      // Close date picker and update filter after selecting end date
      setTimeout(() => {
        setShowDatePicker(false);
        setFilter("custom_date");
      }, 300);
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateRange = () => {
    if (selectedStartDate && selectedEndDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      const endStr = selectedEndDate.toLocaleDateString("en-GB");
      return `${startStr} - ${endStr}`;
    } else if (selectedStartDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      return `${startStr} - `;
    }
    return filterOptions.find((f) => f.value === filter)?.label || "Last 7 Days";
  };

  return (
    <div className="dashboard-card" style={{ height: "425px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <div>
          <h3 className="dashboard-card-title" style={{ marginBottom: "4px" }}>
            Sales Overview
          </h3>
          <p className="dashboard-subtitle">
            Orders and returns trend over time
          </p>
        </div>
        <div ref={dropdownRef} style={{ position: "relative" }}>
          <button
            className="dashboard-btn-filter"
            onClick={() => setIsOpen(!isOpen)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginRight: "8px" }}
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {filter === "custom_date" && selectedStartDate && selectedEndDate
              ? formatDateRange()
              : filterOptions.find((f) => f.value === filter)?.label}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginLeft: "8px" }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {isOpen && (
            <div
              style={{
                position: "absolute",
                top: "48px",
                right: 0,
                background: "#fff",
                border: "1px solid #E5E7EB",
                boxShadow: "0px 10px 30px rgba(0,0,0,0.08)",
                borderRadius: "12px",
                padding: "10px",
                minWidth: "10rem",
                zIndex: 10,
              }}
            >
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (option.value === "custom_date") {
                      setIsOpen(false);
                      setShowDatePicker(true);
                    } else {
                      setFilter(option.value);
                      setSelectedStartDate(null);
                      setSelectedEndDate(null);
                      setIsOpen(false);
                    }
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    border: "none",
                    background:
                      filter === option.value ? "#F3F4F6" : "transparent",
                    borderRadius: "8px",
                    color: "#111827",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <span>{option.label}</span>
                  {filter === option.value && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#2563EB"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Custom Date Picker Popup */}
          {showDatePicker && (
            <>
              <div
                className="dashboard-date-picker-backdrop"
                onClick={() => setShowDatePicker(false)}
              ></div>
              <div className="dashboard-date-picker" ref={datePickerRef}>
                <div className="dashboard-date-picker-header">
                  <button
                    className="dashboard-date-picker-nav-btn"
                    onClick={() => navigateMonth(-1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(90deg)" }}
                    />
                  </button>
                  <h3 className="dashboard-date-picker-month-year">
                    {currentMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </h3>
                  <button
                    className="dashboard-date-picker-nav-btn"
                    onClick={() => navigateMonth(1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(-90deg)" }}
                    />
                  </button>
                </div>

                <div className="dashboard-date-picker-weekdays">
                  <div className="dashboard-weekday">Mo</div>
                  <div className="dashboard-weekday">Tu</div>
                  <div className="dashboard-weekday">We</div>
                  <div className="dashboard-weekday">Th</div>
                  <div className="dashboard-weekday">Fr</div>
                  <div className="dashboard-weekday">Sa</div>
                  <div className="dashboard-weekday">Su</div>
                </div>

                <div className="dashboard-date-picker-days">
                  {getDaysInMonth(currentMonth).map((day, index) => (
                    <div
                      key={index}
                      className={`dashboard-date-picker-day ${
                        day ? "" : "empty"
                      } ${day && isDateDisabled(day) ? "disabled" : ""} ${
                        day && isDateInRange(day) ? "in-range" : ""
                      } ${day && isDateSelected(day) ? "selected" : ""}`}
                      onClick={() => day && handleDateClick(day)}
                    >
                      {day ? day.getDate() : ""}
                    </div>
                  ))}
                </div>

                {selectedStartDate && selectedEndDate && (
                  <div className="dashboard-date-picker-footer">
                    <span className="dashboard-date-picker-selected">
                      {formatDateRange()}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* SalesOverviewGraph */}
      <SalesOverviewGraph series={chartData.series} labels={chartData.labels} />
    </div>
  );
};

export default SalesOverview;
